import { ByteWriter } from './byteWriter';
import {
    Charset,
    SubstitutionLog,
    UTF8_CHARACTER_SET,
    canRepresent,
    charsetFromDeclaration,
    encodeDefaultRepertoire,
    encodeText,
} from './charset';
import { keywordForTag } from './dictionary';
import {
    PATIENT_ID as PATIENT_ID_TAG,
    PATIENT_NAME as PATIENT_NAME_TAG,
    SPECIFIC_CHARACTER_SET as SPECIFIC_CHARACTER_SET_TAG,
} from './tags';
import { DicomDataset, DicomElement, DicomItem } from './dataset';
import { DicomFile, DICM_MAGIC, PREAMBLE_LENGTH } from './reader';
import {
    FILE_META_GROUP_LENGTH_TAG,
    ITEM_DELIMITATION_TAG,
    ITEM_TAG,
    SEQUENCE_DELIMITATION_TAG,
    Tag,
    UNDEFINED_LENGTH,
    formatTag,
    tagElement,
    tagGroup,
} from './tag';
import {
    FILE_META_TRANSFER_SYNTAX,
    IMPLICIT_VR_LITTLE_ENDIAN,
    TransferSyntax,
    transferSyntaxFor,
} from './transferSyntax';
import {
    Vr,
    binaryValueWidth,
    isCharacterSetAware,
    isStringVr,
    paddingByte,
    usesLongFormLength,
} from './vr';

/**
 * The first value length a short-form (2-byte) length field cannot express.
 *
 * PS3.5 gives most VRs a 16-bit length. A value at or above this is written as
 * UN with the 32-bit form instead — the convention dcmjs and the wider
 * ecosystem use, and what makes a contour of more than about 2,700 points
 * storable at all.
 */
const LONG_FORM_LENGTH_THRESHOLD = 0x10000;

/** A DICOM value that cannot be represented in the form it must be written in. */
export class DicomWriteError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'DicomWriteError';
    }
}

/** One identity value the chosen character set cannot carry unchanged. */
export interface UnrepresentableIdentity {
    /** The attribute's dictionary keyword, for naming it to the user. */
    readonly keyword: string;
    /** The value as it stands. */
    readonly value: string;
    /** What writing it in the chosen set would reduce it to. */
    readonly narrowed: string;
}

/**
 * A patient identity value the chosen character set cannot carry.
 *
 * Distinct from the other narrowing this writer performs, because identity is
 * not the system's to approximate: a name reduced to question marks no longer
 * matches the archive it is filed against, and the object stops belonging to
 * the patient it describes. Everything else — a structure name, a label, a
 * description — is narrowed and reported instead.
 *
 * Carries every offending value at once so the user can be shown all of them
 * and decide, rather than being asked about one and then another.
 */
export class PatientIdentityNarrowingError extends DicomWriteError {
    /**
     * The offending values, for the confirmation the user is shown.
     *
     * Non-enumerable: this error reaches generic handlers that log it and
     * forward it to error reporting, and those serialize an Error's own
     * enumerable properties along with its message. The values here are
     * patient identity, so they are reachable by name for the UI that must
     * display them, and invisible to anything that walks the object.
     */
    declare readonly attributes: readonly UnrepresentableIdentity[];
    readonly charset: string;

    constructor(
        attributes: readonly UnrepresentableIdentity[],
        charset: string
    ) {
        // Names the attributes but never their values: the message is logged
        // verbatim, and a patient name in a log is a reportable disclosure.
        super(
            `Patient identity cannot be written in ${charset}: ` +
                `${attributes.map((a) => a.keyword).join(', ')} ` +
                'cannot be represented. Change the value, or accept the ' +
                'reduction explicitly.'
        );
        this.name = 'PatientIdentityNarrowingError';
        this.charset = charset;
        Object.defineProperty(this, 'attributes', {
            value: attributes,
            enumerable: false,
        });
    }
}

/** How the writer treats values the character set cannot carry. */
export interface WritePolicy {
    /**
     * Write patient identity narrowed rather than refusing.
     *
     * Off by default. Set it only where the user has been shown what would
     * change and has accepted it.
     */
    readonly allowIdentityNarrowing?: boolean;

    /**
     * The caller chose the character set this object declares, for a
     * receiving system that requires it.
     *
     * Off by default, and off is the honest default: a set an object arrived
     * carrying was chosen by its sender, not by whoever is saving it now.
     * Set it only where a caller asked for a particular set, which is what
     * makes reducing text to fit that set the price of a transfer rather than
     * silent damage.
     */
    readonly characterSetChosen?: boolean;
}

/**
 * Attributes that identify the patient rather than describe the data.
 *
 * Kept to the two the archive matches on. A structure name is the clinician's
 * label and can be narrowed; these are the patient's identity and cannot be
 * approximated without the object ceasing to name the right person.
 */
const IDENTITY_TAGS: readonly Tag[] = [PATIENT_NAME_TAG, PATIENT_ID_TAG];

/**
 * Refuses a write that would alter patient identity to fit the character set.
 *
 * The question is what writing would change, not which characters the value
 * contains. An element still holding the bytes it was read from is emitted
 * verbatim, so its identity survives whatever set the object declares —
 * including sets this writer has no way to encode into. Checking the decoded
 * string instead made an ordinary save impossible for any patient whose name
 * is not Latin-1, which is a worse failure than the one being prevented.
 *
 * Checked before anything is written, so the caller gets every offending
 * attribute together and no partial file is produced.
 */
function checkIdentityRepresentable(
    dataset: DicomDataset,
    policy: WritePolicy
): void {
    if (policy.allowIdentityNarrowing) {
        return;
    }
    const offending: UnrepresentableIdentity[] = [];
    for (const tag of IDENTITY_TAGS) {
        const element = dataset.element(tag);
        // Written from its original bytes, so nothing about it is re-encoded.
        if (!element || element.unmodifiedBytes !== undefined) {
            continue;
        }
        const charset = element.charsetForWrite;
        for (const value of dataset.strings(tag)) {
            if (canRepresent(value, charset)) {
                continue;
            }
            const probe = new SubstitutionLog();
            encodeText(value, charset, probe);
            offending.push({
                keyword: keywordForTag(tag) ?? formatTag(tag),
                value,
                narrowed: probe.substitutions[0]?.written ?? value,
            });
        }
    }
    if (offending.length > 0) {
        throw new PatientIdentityNarrowingError(
            offending,
            dataset.specificCharset.declared || 'the default repertoire'
        );
    }
}

const UTF8_CHARSET = charsetFromDeclaration(UTF8_CHARACTER_SET);

/** Whether every byte is within the default repertoire. */
function isAsciiOnly(bytes: Uint8Array): boolean {
    return bytes.every((byte) => byte <= 0x7f);
}

/** Whether the text needs more than the default repertoire. */
function exceedsDefaultRepertoire(value: string): boolean {
    // eslint-disable-next-line no-control-regex
    return /[^\u0000-\u007f]/.test(value);
}

/**
 * Whether a value can be written in a character set without being reduced.
 *
 * An absent declaration means the default repertoire, which holds ASCII and
 * nothing else — not the tolerant single-byte encoding the reader falls back
 * to when it has to make sense of an undeclared file's bytes.
 */
function writableIn(value: string, charset: Charset): boolean {
    return charset.declared === ''
        ? !exceedsDefaultRepertoire(value)
        : canRepresent(value, charset);
}

/**
 * Whether text this write would encode cannot be held by the set in force.
 *
 * Judged only on values that will be encoded. An element still holding its
 * source bytes is written back exactly as it arrived, so it is already
 * representable in whatever the object declares and can never be what forces
 * the declaration to change — asking otherwise would rewrite a file on a save
 * that edited nothing about it.
 */
function encodedTextNeedsWiderCharset(dataset: DicomDataset): boolean {
    for (const element of dataset.orderedElements()) {
        if (element.vr === 'SQ') {
            const content = element.value;
            if (
                content.kind === 'sequence' &&
                content.items.some((item) =>
                    encodedTextNeedsWiderCharset(item.dataset)
                )
            ) {
                return true;
            }
            continue;
        }
        if (
            !isCharacterSetAware(element.vr) ||
            element.unmodifiedBytes !== undefined
        ) {
            continue;
        }
        const content = element.value;
        if (
            content.kind === 'strings' &&
            content.values.some(
                (value) => !writableIn(value, element.charsetForWrite)
            )
        ) {
            return true;
        }
    }
    return false;
}

/**
 * Re-encodes character-set-aware text as UTF-8.
 *
 * An element whose source bytes are already within the default repertoire is
 * left untouched: ASCII is a subset of UTF-8, so those bytes are already
 * correct under the new declaration and keep their byte-for-byte guarantee.
 * Everything else is rebuilt from its decoded value, which is what moves a
 * value that arrived as a high byte under no declaration into the set the
 * object will now claim.
 */
function reencodeAsUtf8(dataset: DicomDataset): void {
    for (const element of dataset.orderedElements()) {
        if (element.vr === 'SQ') {
            const content = element.value;
            if (content.kind === 'sequence') {
                for (const item of content.items) {
                    reencodeAsUtf8(item.dataset);
                }
            }
            continue;
        }
        if (!isCharacterSetAware(element.vr)) {
            continue;
        }
        const source = element.unmodifiedBytes;
        if (source !== undefined && isAsciiOnly(source)) {
            continue;
        }
        const content = element.value;
        if (content.kind !== 'strings') {
            continue;
        }
        dataset.put(
            DicomElement.fromStrings(
                element.tag,
                element.vr,
                content.values,
                UTF8_CHARSET
            )
        );
    }
}

/**
 * Declares UTF-8 where text would otherwise have to be reduced to fit.
 *
 * Reducing text loses information, so it is only ever right where someone
 * chose the set that forces it. A caller exporting for a receiving system
 * that requires a legacy set has made that choice, and the reduction is the
 * price of the transfer. Nobody else has: a set the object merely arrived
 * carrying was chosen by whoever sent it, and an absent set was chosen by no
 * one at all, so neither is a reason to damage text this user typed.
 *
 * Where no one chose, the declaration is raised and the text kept. The whole
 * dataset is re-encoded with it, because a declaration is a statement about
 * every value in the object, not only the one that provoked the change.
 *
 * The dataset is changed rather than a copy of it, so that what it declares
 * afterwards agrees with the bytes just written for it.
 */
function declareUtf8IfTextNeedsIt(
    dataset: DicomDataset,
    policy: WritePolicy
): void {
    if (policy.characterSetChosen) {
        return;
    }
    if (!encodedTextNeedsWiderCharset(dataset)) {
        return;
    }
    reencodeAsUtf8(dataset);
    dataset.setString(SPECIFIC_CHARACTER_SET_TAG, UTF8_CHARACTER_SET, 'CS');
}

/**
 * Serializes a parsed file back to DICOM Part 10 bytes.
 *
 * An attribute nobody edited is written from the bytes it was read from, so a
 * read/write cycle over an untouched file reproduces it exactly. That is the
 * property that makes the output checkable: a diff against the source is
 * empty unless the caller changed something, and whatever it shows is
 * precisely what the caller changed.
 *
 * Only the file meta group length is always recomputed. It describes the
 * bytes being written now, and carrying a stale one forward would emit a file
 * whose own header disagrees with its content.
 */
export function writeDicomFile(
    file: DicomFile,
    log?: SubstitutionLog,
    policy: WritePolicy = {}
): ArrayBuffer {
    // Before the identity check: a name the set in force cannot carry is
    // carried by raising the declaration, not refused. The refusal is for a
    // set the caller chose.
    declareUtf8IfTextNeedsIt(file.dataset, policy);
    checkIdentityRepresentable(file.dataset, policy);

    const writer = new ByteWriter();

    writePreamble(writer, file.preamble);
    writeFileMetaGroup(writer, file.meta, log);
    writeDataset(writer, file.dataset, file.transferSyntax, log);

    return writer.toArrayBuffer();
}

function writePreamble(writer: ByteWriter, preamble: Uint8Array): void {
    if (preamble.byteLength === PREAMBLE_LENGTH) {
        writer.writeBytes(preamble);
    } else {
        // A file assembled in memory rather than read has no preamble of its
        // own; PS3.10 lets it be all zeroes when no application profile
        // claims it.
        writer.writeRepeated(0x00, PREAMBLE_LENGTH);
    }
    writer.writeAscii(DICM_MAGIC);
}

/**
 * Writes the group-0002 file meta with a freshly computed group length.
 *
 * The group is always explicit VR little endian, whatever transfer syntax it
 * names for the dataset that follows it.
 */
function writeFileMetaGroup(
    writer: ByteWriter,
    meta: DicomDataset,
    log?: SubstitutionLog
): void {
    const body = new ByteWriter(1024);
    for (const element of meta.orderedElements()) {
        if (element.tag === FILE_META_GROUP_LENGTH_TAG) {
            continue;
        }
        if (tagGroup(element.tag) !== 0x0002) {
            throw new DicomWriteError(
                `Element ${formatTag(element.tag)} is not a file meta attribute ` +
                    'but was placed in the file meta group.'
            );
        }
        writeElement(body, element, FILE_META_TRANSFER_SYNTAX, log);
    }

    writeElementHeader(
        writer,
        FILE_META_GROUP_LENGTH_TAG,
        'UL',
        4,
        FILE_META_TRANSFER_SYNTAX
    );
    writer.writeUint32(body.length, true);
    writer.writeBytes(body.toBytes());
}

/** Writes a dataset's elements in ascending tag order, as PS3.5 requires. */
function writeDataset(
    writer: ByteWriter,
    dataset: DicomDataset,
    syntax: TransferSyntax,
    log?: SubstitutionLog
): void {
    for (const element of dataset.orderedElements()) {
        writeElement(writer, element, syntax, log);
    }
}

function writeElement(
    writer: ByteWriter,
    element: DicomElement,
    syntax: TransferSyntax,
    log?: SubstitutionLog
): void {
    // An element nobody edited is written from the bytes it was read from, so
    // its value is never needed. Asking for it first decoded it anyway — and
    // decoding is where a parse's cost is: every contour point parsed out of
    // its decimal string, and then cached, to write back the bytes those
    // numbers were read from.
    const unmodified = element.unmodifiedBytes;
    if (unmodified !== undefined) {
        writeElementHeader(
            writer,
            element.tag,
            element.vrForWrite,
            unmodified.byteLength,
            syntax
        );
        writer.writeBytes(unmodified);
        return;
    }

    const content = element.value;

    if (content.kind === 'sequence') {
        writeSequence(
            writer,
            element.tag,
            element.vrForWrite,
            content.items,
            content.undefinedLength,
            syntax,
            log
        );
        return;
    }

    if (content.kind === 'fragments') {
        writeEncapsulatedPixelData(
            writer,
            element.tag,
            element.vr,
            content.value.offsetTable,
            content.value.fragments,
            syntax
        );
        return;
    }

    const value = encodeValue(element, log);
    writeElementHeader(
        writer,
        element.tag,
        element.vrForWrite,
        value.byteLength,
        syntax
    );
    writer.writeBytes(value);
}

/**
 * Writes a tag, VR and length.
 *
 * The VR is written only in an explicit-VR stream. In an implicit-VR stream
 * the dictionary supplies it on the way back in, which is why an attribute
 * the dictionary does not know still round-trips: nothing about it is
 * recorded on the wire.
 */
function writeElementHeader(
    writer: ByteWriter,
    tag: Tag,
    vr: Vr,
    length: number,
    syntax: TransferSyntax
): void {
    writer.writeUint16(tagGroup(tag), syntax.littleEndian);
    writer.writeUint16(tagElement(tag), syntax.littleEndian);

    if (!syntax.explicitVr) {
        writer.writeUint32(length, syntax.littleEndian);
        return;
    }

    // A value too long for this VR's own length field is written as UN, whose
    // length field is 32 bits. The reader substitutes the dictionary VR back,
    // so the value is still read as what it is; refusing instead would make a
    // large contour unsavable.
    const tooLongForItsVr =
        !usesLongFormLength(vr) &&
        length >= LONG_FORM_LENGTH_THRESHOLD &&
        length !== UNDEFINED_LENGTH;
    const written = tooLongForItsVr ? 'UN' : vr;

    writer.writeAscii(written);
    if (usesLongFormLength(written)) {
        writer.writeUint16(0, syntax.littleEndian); // Reserved.
        writer.writeUint32(length, syntax.littleEndian);
        return;
    }
    writer.writeUint16(length, syntax.littleEndian);
}

function writeSequence(
    writer: ByteWriter,
    tag: Tag,
    vr: Vr,
    items: readonly DicomItem[],
    undefinedLength: boolean,
    syntax: TransferSyntax,
    log?: SubstitutionLog
): void {
    // A sequence that arrived as an undefined-length UN element holds
    // implicit-VR items whatever the enclosing syntax; see PS3.5 section 7.5.
    const itemSyntax =
        vr === 'UN' ? transferSyntaxFor(IMPLICIT_VR_LITTLE_ENDIAN) : syntax;

    if (undefinedLength) {
        writeElementHeader(writer, tag, vr, UNDEFINED_LENGTH, syntax);
        for (const item of items) {
            writeItem(writer, item, itemSyntax, log);
        }
        writeDelimiter(writer, SEQUENCE_DELIMITATION_TAG, syntax);
        return;
    }

    const body = new ByteWriter(4096);
    for (const item of items) {
        writeItem(body, item, itemSyntax, log);
    }
    writeElementHeader(writer, tag, vr, body.length, syntax);
    writer.writeBytes(body.toBytes());
}

function writeItem(
    writer: ByteWriter,
    item: DicomItem,
    syntax: TransferSyntax,
    log?: SubstitutionLog
): void {
    if (item.undefinedLength) {
        writeItemHeader(writer, UNDEFINED_LENGTH, syntax);
        writeDataset(writer, item.dataset, syntax, log);
        writeDelimiter(writer, ITEM_DELIMITATION_TAG, syntax);
        return;
    }

    const body = new ByteWriter(4096);
    writeDataset(body, item.dataset, syntax, log);
    writeItemHeader(writer, body.length, syntax);
    writer.writeBytes(body.toBytes());
}

/** Item and delimiter tags never carry a VR, in either transfer syntax. */
function writeItemHeader(
    writer: ByteWriter,
    length: number,
    syntax: TransferSyntax
): void {
    writer.writeUint16(tagGroup(ITEM_TAG), syntax.littleEndian);
    writer.writeUint16(tagElement(ITEM_TAG), syntax.littleEndian);
    writer.writeUint32(length, syntax.littleEndian);
}

function writeDelimiter(
    writer: ByteWriter,
    tag: Tag,
    syntax: TransferSyntax
): void {
    writer.writeUint16(tagGroup(tag), syntax.littleEndian);
    writer.writeUint16(tagElement(tag), syntax.littleEndian);
    writer.writeUint32(0, syntax.littleEndian);
}

/**
 * Writes encapsulated PixelData: always undefined length, with the Basic
 * Offset Table as the first item and each frame's fragment after it
 * (PS3.5 Annex A.4).
 */
function writeEncapsulatedPixelData(
    writer: ByteWriter,
    tag: Tag,
    vr: Vr,
    offsetTable: Uint8Array,
    fragments: readonly Uint8Array[],
    syntax: TransferSyntax
): void {
    writeElementHeader(writer, tag, vr, UNDEFINED_LENGTH, syntax);

    writeItemHeader(writer, offsetTable.byteLength, syntax);
    writer.writeBytes(offsetTable);

    for (const fragment of fragments) {
        if (fragment.byteLength % 2 !== 0) {
            throw new DicomWriteError(
                `A pixel data fragment is ${fragment.byteLength} bytes; fragments ` +
                    'must have an even length.'
            );
        }
        writeItemHeader(writer, fragment.byteLength, syntax);
        writer.writeBytes(fragment);
    }

    writeDelimiter(writer, SEQUENCE_DELIMITATION_TAG, syntax);
}

/**
 * Encodes an element whose value was set rather than read, padding it to the
 * even length PS3.5 requires.
 */
function encodeValue(element: DicomElement, log?: SubstitutionLog): Uint8Array {
    const content = element.value;
    switch (content.kind) {
        case 'strings':
            return padToEven(
                encodeStrings(
                    content.values,
                    element.vr,
                    element.charsetForWrite,
                    log
                ),
                paddingByte(element.vr)
            );
        case 'numbers':
            return encodeNumbers(content.values, element.vr);
        case 'bigints':
            return encodeBigInts(content.values, element.vr);
        case 'bytes':
            return padToEven(content.bytes, paddingByte(element.vr));
        default:
            /* istanbul ignore next -- sequences and fragments never reach here */
            throw new DicomWriteError(
                `Element ${formatTag(element.tag)} holds ${content.kind}, which is ` +
                    'written by its own encoder rather than as a value field.'
            );
    }
}

function encodeStrings(
    values: readonly string[],
    vr: Vr,
    charset: Charset,
    log?: SubstitutionLog
): Uint8Array {
    if (!isStringVr(vr)) {
        throw new DicomWriteError(
            `VR ${vr} does not hold character data but was given string values.`
        );
    }
    const joined = values.join('\\');
    return isCharacterSetAware(vr)
        ? encodeText(joined, charset, log)
        : encodeDefaultRepertoire(joined, log);
}

function encodeNumbers(values: readonly number[], vr: Vr): Uint8Array {
    if (vr === 'DS' || vr === 'IS') {
        return padToEven(
            encodeDefaultRepertoire(values.map(formatNumeric(vr)).join('\\')),
            paddingByte(vr)
        );
    }
    const width = binaryValueWidth(vr);
    if (width === undefined) {
        throw new DicomWriteError(
            `VR ${vr} has no fixed-width binary form but was given numeric values.`
        );
    }
    const bytes = new Uint8Array(values.length * width);
    const view = new DataView(bytes.buffer);
    values.forEach((value, index) => {
        const at = index * width;
        switch (vr) {
            case 'FL':
                view.setFloat32(at, value, true);
                break;
            case 'FD':
                view.setFloat64(at, value, true);
                break;
            case 'SL':
                view.setInt32(at, value, true);
                break;
            case 'SS':
                view.setInt16(at, value, true);
                break;
            case 'UL':
                view.setUint32(at, value, true);
                break;
            case 'US':
                view.setUint16(at, value, true);
                break;
            case 'AT':
                view.setUint16(at, (value >>> 16) & 0xffff, true);
                view.setUint16(at + 2, value & 0xffff, true);
                break;
            default:
                throw new DicomWriteError(`VR ${vr} has no numeric encoding.`);
        }
    });
    return bytes;
}

/**
 * Renders a number for the DS and IS string VRs.
 *
 * IS is an integer and is rejected rather than rounded if it is not one: a
 * silently truncated ReferencedFrameNumber or ROINumber is a wrong reference,
 * not a formatting detail.
 *
 * DS keeps at most the 16 characters PS3.5 allows, and reaches that limit by
 * re-spelling the number in exponential notation — which PS3.5 permits and
 * which rounds — rather than by cutting characters off the end. Truncating
 * the text of a decimal string is not the same as rounding the number: it
 * always moves toward zero, and applied to something like DoseGridScaling it
 * silently changes what every dose value in the file means.
 */
export function formatNumericValue(value: number, vr: Vr): string {
    return formatNumeric(vr)(value);
}

function formatNumeric(vr: Vr): (value: number) => string {
    if (vr === 'IS') {
        return (value) => {
            if (!Number.isInteger(value)) {
                throw new DicomWriteError(
                    `Value ${value} is not an integer and cannot be written as IS.`
                );
            }
            return String(value);
        };
    }
    return (value) => {
        const plain = String(value);
        if (plain.length <= 16) {
            return plain;
        }
        const exponential = value.toExponential(9);
        return exponential.length <= 16 ? exponential : value.toExponential(6);
    };
}

function encodeBigInts(values: readonly bigint[], vr: Vr): Uint8Array {
    const bytes = new Uint8Array(values.length * 8);
    const view = new DataView(bytes.buffer);
    values.forEach((value, index) => {
        if (vr === 'SV') {
            view.setBigInt64(index * 8, value, true);
        } else {
            view.setBigUint64(index * 8, value, true);
        }
    });
    return bytes;
}

function padToEven(bytes: Uint8Array, padding: number): Uint8Array {
    if (bytes.byteLength % 2 === 0) {
        return bytes;
    }
    const padded = new Uint8Array(bytes.byteLength + 1);
    padded.set(bytes);
    padded[bytes.byteLength] = padding;
    return padded;
}
